import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import LeaveStatusBadge from './LeaveStatusBadge';
import { LEAVE_TYPE_LABELS, LEAVE_STATUS } from '../constants';


const LeaveTable = ({ 
  leaves, 
  onCancelLeave, 
  onEditLeave, 
  showEmployee = false, 
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const navigate = useNavigate();


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {showEmployee && <TableCell>Employee</TableCell>}
              <TableCell>Leave Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Days</TableCell>
              <TableCell>Status</TableCell>
              {showEmployee && <TableCell>Reviewed By</TableCell>}
              {showEmployee && <TableCell>Reviewed Date</TableCell>}
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaves && leaves.length > 0 ? (
              leaves.map((leave) => (
                <TableRow
                  key={leave.id}
                  hover
                  onClick={() => {
                    navigate(`/leaves/${leave.id}`);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  {showEmployee && (
                    <TableCell>
                      {leave.employee?.userName || leave.employee?.email || '-'}
                    </TableCell>
                  )}
                  <TableCell>
                    {LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType}
                    {leave.isHalfDay && ` (Half Day)`}
                  </TableCell>
                  <TableCell>{formatDate(leave.startDate)}</TableCell>
                  <TableCell>{formatDate(leave.endDate)}</TableCell>
                  <TableCell>{leave.totalDays}</TableCell>
                  <TableCell>
                    <LeaveStatusBadge status={leave.status} />
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {leave.approver && leave.approver.id === currentUserId
                        ? 'Me'
                        : (leave.approver?.userName || leave.approver?.email || '-')}
                    </TableCell>
                  )}
                  {showEmployee && (
                    <TableCell>{formatDate(leave.reviewedAt || leave.approvedAt)}</TableCell>
                  )}
                  <TableCell align="right">
                    {!showEmployee && leave.status === LEAVE_STATUS.PENDING && leave.userId === currentUserId && (
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {checkComponentPermission('UPDATE_LEAVE') && (
                          <Tooltip title="Edit Leave">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditLeave(leave.id);
                              }}
                            >
                              <Iconify icon="eva:edit-fill" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {checkComponentPermission('DELETE_LEAVE') && (
                          <Tooltip title="Delete Leave">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCancelLeave(leave.id);
                              }}
                            >
                              <Iconify icon="material-symbols:delete" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={showEmployee ? 9 : 6} align="center">
                  No leave requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

LeaveTable.propTypes = {
  leaves: PropTypes.array.isRequired,
  onCancelLeave: PropTypes.func,
  onEditLeave: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
};

export default LeaveTable;

